import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Page,
  Card,
  BlockStack,
  Text,
  InlineGrid,
  InlineStack,
  Banner,
  EmptyState,
  Box,
  Badge,
  ProgressBar,
  Divider,
  Select,
} from '@shopify/polaris';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import type { AnalyticsDashboard } from '@freebi/shared';
import { useApi } from '../providers/ApiProvider';
import { useStore } from '../providers/StoreProvider';
import { StatCard } from '../components/StatCard';
import { t } from '../i18n';

/** Shopify Admin–style report date ranges */
type PeriodKey =
  | 'today'
  | 'yesterday'
  | 'last7'
  | 'last30'
  | 'last90'
  | 'last365'
  | 'wtd'
  | 'mtd'
  | 'qtd'
  | 'ytd';

const PERIOD_OPTIONS: Array<{ label: string; value: PeriodKey }> = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 days', value: 'last7' },
  { label: 'Last 30 days', value: 'last30' },
  { label: 'Last 90 days', value: 'last90' },
  { label: 'Last 12 months', value: 'last365' },
  { label: 'Week to date', value: 'wtd' },
  { label: 'Month to date', value: 'mtd' },
  { label: 'Quarter to date', value: 'qtd' },
  { label: 'Year to date', value: 'ytd' },
];

function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function toDateParam(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function rangeForPeriod(key: PeriodKey): { from: string; to: string } {
  const now = new Date();
  const todayStart = startOfLocalDay(now);
  const todayEnd = endOfLocalDay(now);

  switch (key) {
    case 'today':
      return { from: toDateParam(todayStart), to: toDateParam(todayEnd) };
    case 'yesterday': {
      const y = new Date(todayStart);
      y.setDate(y.getDate() - 1);
      return { from: toDateParam(y), to: toDateParam(endOfLocalDay(y)) };
    }
    case 'last7': {
      const from = new Date(todayStart);
      from.setDate(from.getDate() - 6);
      return { from: toDateParam(from), to: toDateParam(todayEnd) };
    }
    case 'last30': {
      const from = new Date(todayStart);
      from.setDate(from.getDate() - 29);
      return { from: toDateParam(from), to: toDateParam(todayEnd) };
    }
    case 'last90': {
      const from = new Date(todayStart);
      from.setDate(from.getDate() - 89);
      return { from: toDateParam(from), to: toDateParam(todayEnd) };
    }
    case 'last365': {
      const from = new Date(todayStart);
      from.setDate(from.getDate() - 364);
      return { from: toDateParam(from), to: toDateParam(todayEnd) };
    }
    case 'wtd': {
      const from = new Date(todayStart);
      const day = from.getDay();
      const diff = day === 0 ? 6 : day - 1;
      from.setDate(from.getDate() - diff);
      return { from: toDateParam(from), to: toDateParam(todayEnd) };
    }
    case 'mtd': {
      const from = new Date(todayStart.getFullYear(), todayStart.getMonth(), 1);
      return { from: toDateParam(from), to: toDateParam(todayEnd) };
    }
    case 'qtd': {
      const q = Math.floor(todayStart.getMonth() / 3);
      const from = new Date(todayStart.getFullYear(), q * 3, 1);
      return { from: toDateParam(from), to: toDateParam(todayEnd) };
    }
    case 'ytd': {
      const from = new Date(todayStart.getFullYear(), 0, 1);
      return { from: toDateParam(from), to: toDateParam(todayEnd) };
    }
    default:
      return rangeForPeriod('last30');
  }
}

function formatRangeLabel(from: string, to: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  const a = new Date(from + 'T12:00:00');
  const b = new Date(to + 'T12:00:00');
  if (from === to) return a.toLocaleDateString(undefined, opts);
  return `${a.toLocaleDateString(undefined, opts)} – ${b.toLocaleDateString(undefined, opts)}`;
}

function shortDayLabel(isoDate: string, period: PeriodKey): string {
  const d = new Date(isoDate + 'T12:00:00');
  if (period === 'last365' || period === 'ytd') {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function AnalyticsPage() {
  const api = useApi();
  const { formatMoney } = useStore();
  const [period, setPeriod] = useState<PeriodKey>('last30');
  const [data, setData] = useState<AnalyticsDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => rangeForPeriod(period), [period]);
  const periodLabel =
    PERIOD_OPTIONS.find((o) => o.value === period)?.label || 'Last 30 days';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({
        from: range.from,
        to: range.to,
      });
      const d = await api.get<AnalyticsDashboard>(
        `/analytics/dashboard?${q.toString()}`,
      );
      setData(d);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [api, range.from, range.to]);

  useEffect(() => {
    void load();
  }, [load]);

  const fmt = (n: number) => formatMoney(n, { maximumFractionDigits: 0 });
  const stats = data?.stats;
  const topCampaigns = data?.topCampaigns || [];
  const maxRevenue = Math.max(
    1,
    ...topCampaigns.map((c) => Number(c.revenue) || 0),
  );
  const hasFreebiActivity =
    !!stats &&
    (stats.giftRedemption > 0 ||
      stats.ordersInfluenced > 0 ||
      stats.revenueGenerated > 0);

  /** Full day series for selected filter — zeros for days with no data (Shopify-style) */
  const chartData = useMemo(() => {
    const byDay = new Map<string, number>();
    for (const t of data?.trends || []) {
      byDay.set(t.date, Number(t.conversions) || 0);
    }
    const points: Array<{ date: string; label: string; orders: number }> = [];
    const start = new Date(range.from + 'T12:00:00');
    const end = new Date(range.to + 'T12:00:00');
    const dayMs = 86400000;
    const days = Math.max(
      1,
      Math.round((end.getTime() - start.getTime()) / dayMs) + 1,
    );
    const limit = Math.min(days, 366);
    for (let i = 0; i < limit; i++) {
      const d = new Date(start.getTime() + i * dayMs);
      const iso = toDateParam(d);
      points.push({
        date: iso,
        label: shortDayLabel(iso, period),
        orders: byDay.get(iso) ?? 0,
      });
    }
    return points;
  }, [data?.trends, period, range.from, range.to]);

  const totalChartOrders = chartData.reduce((s, d) => s + d.orders, 0);
  const maxOrders = Math.max(0, ...chartData.map((d) => d.orders));
  // Nice Y-axis like Shopify analytics cards
  const yMax = useMemo(() => {
    if (maxOrders <= 0) return 10;
    if (maxOrders <= 10) return Math.max(10, Math.ceil(maxOrders / 2) * 2);
    if (maxOrders <= 50) return Math.ceil(maxOrders / 5) * 5;
    if (maxOrders <= 100) return Math.ceil(maxOrders / 10) * 10;
    return Math.ceil(maxOrders / 25) * 25;
  }, [maxOrders]);
  const yTicks = useMemo(() => {
    const step =
      yMax <= 10 ? 2 : yMax <= 50 ? 5 : yMax <= 100 ? 10 : Math.ceil(yMax / 5);
    const ticks: number[] = [];
    for (let t = 0; t <= yMax; t += step) ticks.push(t);
    if (ticks[ticks.length - 1] !== yMax) ticks.push(yMax);
    return ticks;
  }, [yMax]);

  return (
    <Page title={t("Analytics")}>
      <BlockStack gap="500">
        {/* Date range — own row (Shopify-style filter, not crammed into title) */}
        <Card>
          <InlineStack align="space-between" blockAlign="center" wrap gap="400">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">
                {t("Freebi performance")}
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                {formatRangeLabel(range.from, range.to)}
              </Text>
            </BlockStack>
            <div style={{ minWidth: 200, maxWidth: 260, width: '100%' }}>
              <Select
                label={t("Date range")}
                labelHidden
                options={PERIOD_OPTIONS}
                value={period}
                onChange={(v) => setPeriod(v as PeriodKey)}
              />
            </div>
          </InlineStack>
        </Card>

        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}

        <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
          <StatCard
            title={t("Influenced revenue")}
            value={loading ? '…' : fmt(stats?.revenueGenerated ?? 0)}
          />
          <StatCard
            title={t("Orders influenced")}
            value={loading ? '…' : String(stats?.ordersInfluenced ?? 0)}
          />
          <StatCard
            title={t("Gifts claimed")}
            value={loading ? '…' : String(stats?.giftRedemption ?? 0)}
            hint="Free gifts in completed orders"
          />
          <StatCard
            title={t("Influenced AOV")}
            value={
              loading
                ? '…'
                : stats && stats.ordersInfluenced > 0
                  ? fmt(stats.revenueGenerated / stats.ordersInfluenced)
                  : fmt(0)
            }
          />
        </InlineGrid>

        <InlineGrid columns={{ xs: 1, sm: 2 }} gap="400">
          <StatCard
            title={t("Active campaigns")}
            value={loading ? '…' : String(stats?.activeCampaigns ?? 0)}
          />
          <StatCard
            title={t("Orders with a free gift")}
            value={
              loading
                ? '…'
                : `${(stats?.conversionRate ?? 0).toFixed(0)}%`
            }
            hint="Share of influenced orders that included a Freebi freebie"
          />
        </InlineGrid>

        {/* Orders by day — layout mirrors Shopify Analytics report cards */}
        <Card padding="0">
          <Box padding="400">
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="start" wrap>
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">
                    {t("Total orders")}
                  </Text>
                  {!loading && (
                    <Text as="p" variant="headingLg" fontWeight="bold">
                      {totalChartOrders.toLocaleString()}
                    </Text>
                  )}
                </BlockStack>
                <Text as="p" tone="subdued" variant="bodySm">
                  {t("by day")}
                </Text>
              </InlineStack>

              <div style={{ width: '100%', height: 260 }}>
                {loading ? (
                  <div
                    style={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text as="p" tone="subdued">
                      {t("Loading…")}
                    </Text>
                  </div>
                ) : (
                  <ResponsiveContainer>
                    <BarChart
                      data={chartData}
                      margin={{ top: 4, right: 4, left: -8, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="0"
                        stroke="#E4E5E7"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: '#8C9196' }}
                        interval="preserveStartEnd"
                        minTickGap={28}
                      />
                      <YAxis
                        domain={[0, yMax]}
                        ticks={yTicks}
                        allowDecimals={false}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: '#8C9196' }}
                        width={32}
                      />
                      <Tooltip
                        cursor={{ fill: 'rgba(13, 122, 95, 0.06)' }}
                        formatter={(value: number) => [
                          `${value} order${value === 1 ? '' : 's'}`,
                          'Orders',
                        ]}
                        labelFormatter={(_, payload) => {
                          const row = payload?.[0]?.payload as
                            | { date?: string }
                            | undefined;
                          return row?.date
                            ? formatRangeLabel(row.date, row.date)
                            : '';
                        }}
                        contentStyle={{
                          borderRadius: 8,
                          border: '1px solid #E3E5E8',
                          boxShadow: '0 4px 12px rgba(0,0,0,.08)',
                          fontSize: 13,
                        }}
                      />
                      <Bar
                        dataKey="orders"
                        name="Orders"
                        fill="#2C6ECB"
                        radius={[3, 3, 0, 0]}
                        maxBarSize={
                          chartData.length > 60
                            ? 8
                            : chartData.length > 30
                              ? 16
                              : 40
                        }
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </BlockStack>
          </Box>
        </Card>

        {!loading && !hasFreebiActivity && (
          <Card>
            <EmptyState
              heading={t("No Freebi order activity yet")}
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>
                {t("Metrics fill in when customers complete orders with Freebi free\n                gifts or discount codes in")} {periodLabel.toLowerCase()}.
              </p>
            </EmptyState>
          </Card>
        )}

        {topCampaigns.length > 0 && (
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center" wrap>
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">
                    {t("Top campaigns")}
                  </Text>
                  <Text as="p" tone="subdued" variant="bodySm">
                    {t("Ranked by influenced revenue ·")} {periodLabel.toLowerCase()}
                  </Text>
                </BlockStack>
                <Badge>{`${topCampaigns.length} campaign${topCampaigns.length === 1 ? '' : 's'}`}</Badge>
              </InlineStack>

              <BlockStack gap="0">
                {topCampaigns.map((c, index) => {
                  const revenue = Number(c.revenue) || 0;
                  const views = Number(c.impressions) || 0;
                  const orders = Number(c.conversions) || 0;
                  const barPct = Math.round((revenue / maxRevenue) * 100);
                  const isLast = index === topCampaigns.length - 1;

                  return (
                    <div key={c.campaignId}>
                      <Box paddingBlock="300">
                        <BlockStack gap="200">
                          <InlineStack
                            align="space-between"
                            blockAlign="start"
                            wrap
                            gap="300"
                          >
                            <InlineStack gap="300" blockAlign="center" wrap>
                              <div
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 8,
                                  background:
                                    index === 0
                                      ? '#0D7A5F'
                                      : index === 1
                                        ? '#4A7C6F'
                                        : '#E3E5E8',
                                  color: index < 2 ? '#fff' : '#5C5F62',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 13,
                                  fontWeight: 700,
                                  flexShrink: 0,
                                }}
                              >
                                {index + 1}
                              </div>
                              <BlockStack gap="050">
                                <Text as="span" fontWeight="semibold">
                                  {c.campaignName || 'Untitled campaign'}
                                </Text>
                                <Text as="span" tone="subdued" variant="bodySm">
                                  {orders.toLocaleString()} order
                                  {orders === 1 ? '' : 's'}
                                  {views > 0
                                    ? ` · ${views.toLocaleString()} views`
                                    : ''}
                                </Text>
                              </BlockStack>
                            </InlineStack>
                            <BlockStack gap="050">
                              <Text
                                as="span"
                                fontWeight="semibold"
                                alignment="end"
                              >
                                {fmt(revenue)}
                              </Text>
                              <Text
                                as="span"
                                tone="subdued"
                                variant="bodySm"
                                alignment="end"
                              >
                                {t("influenced revenue")}
                              </Text>
                            </BlockStack>
                          </InlineStack>
                          <ProgressBar
                            progress={barPct}
                            size="small"
                            tone={index === 0 ? 'success' : 'primary'}
                          />
                        </BlockStack>
                      </Box>
                      {!isLast && <Divider />}
                    </div>
                  );
                })}
              </BlockStack>
            </BlockStack>
          </Card>
        )}
      </BlockStack>
    </Page>
  );
}
